import { useEffect, useState, type ReactNode } from "react";
import { Check, Copy, ExternalLink, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { Field } from "@/components/forms";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { defaultAppleRedirectUri } from "@/components/auth/SocialAuthButtons";

interface SettingField {
  key: string;
  value: string;
  is_set: boolean;
  source: "database" | "environment" | "unset";
}

const SOURCE_LABEL: Record<SettingField["source"], string> = {
  database: "Saved here",
  environment: "From server env",
  unset: "Not set",
};

function CopyValue({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex min-w-0 items-center gap-2 rounded-2xl bg-muted px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="truncate font-mono text-[13px] text-foreground">{value}</p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-8 shrink-0"
        aria-label={`Copy ${label}`}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            toast.error("Couldn't copy. Select the text and copy it instead.");
          }
        }}
      >
        {copied ? <Check /> : <Copy />}
      </Button>
    </div>
  );
}

function ProviderCard({
  title,
  status,
  steps,
  docsHref,
  children,
}: {
  title: string;
  status: boolean;
  steps: ReactNode[];
  docsHref: string;
  children: ReactNode;
}) {
  return (
    <section className="space-y-5 rounded-2xl border border-border p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[17px] font-medium text-foreground">{title}</h3>
        <Badge variant={status ? "success" : "outline"} dot>
          {status ? "On" : "Off"}
        </Badge>
      </div>
      <ol className="list-decimal space-y-1.5 pl-5 text-sm leading-6 text-body marker:text-muted-foreground">
        {steps.map((s, i) => (
          <li key={i}>{s}</li>
        ))}
      </ol>
      <a
        href={docsHref}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
      >
        Open the setup guide <ExternalLink className="size-3.5" />
      </a>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

/**
 * Google and Apple sign-in setup. Each provider turns on as soon as its client
 * ID is saved; clearing it turns the provider off again (unless the server's
 * environment sets one). Saved on its own, not by Deploy changes.
 */
export default function SocialAuthSettings() {
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<SettingField[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const origin = typeof window === "undefined" ? "" : window.location.origin;
  const appleReturn = defaultAppleRedirectUri();

  const applyFields = (next: SettingField[]) => {
    setFields(next);
    setDraft(Object.fromEntries(next.map((f) => [f.key, f.source === "database" ? f.value : ""])));
  };

  useEffect(() => {
    apiClient
      .get("/admin/settings/social-auth")
      .then((res) => applyFields(res.data?.data ?? []))
      .catch(() => toast.error("Couldn't load sign-in settings."))
      .finally(() => setLoading(false));
  }, []);

  const field = (key: string) => fields.find((f) => f.key === key);
  const savedValue = (key: string) => {
    const f = field(key);
    return f?.source === "database" ? f.value : "";
  };
  const isDirty = fields.some((f) => (draft[f.key] ?? "").trim() !== savedValue(f.key));

  const handleSave = async () => {
    setSaving(true);
    try {
      const values: Record<string, string> = {};
      for (const f of fields) {
        const v = (draft[f.key] ?? "").trim();
        if (v !== savedValue(f.key)) values[f.key] = v;
      }
      const res = await apiClient.put(
        "/admin/settings/social-auth",
        { values },
        { skipGlobalErrorHandler: true } as any,
      );
      applyFields(res.data?.data ?? []);
      queryClient.invalidateQueries({ queryKey: ["social-auth-config"] });
      toast.success("Sign-in settings saved.");
    } catch (err: any) {
      toast.error("Couldn't save sign-in settings", {
        description: err?.response?.data?.message || err?.response?.data?.error,
      });
    } finally {
      setSaving(false);
    }
  };

  const input = (key: string, label: string, placeholder: string, hint: ReactNode) => {
    const f = field(key);
    return (
      <Field
        label={label}
        labelAction={
          f && (
            <Badge variant={f.is_set ? "success" : "outline"}>{SOURCE_LABEL[f.source]}</Badge>
          )
        }
        hint={
          f?.source === "environment" ? (
            <>
              Using <span className="font-mono">{f.value}</span> from the server. Enter a value to
              override it.
            </>
          ) : (
            hint
          )
        }
      >
        <Input
          autoComplete="off"
          spellCheck={false}
          className="font-mono text-[13px]"
          placeholder={placeholder}
          value={draft[key] ?? ""}
          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
        />
      </Field>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" role="status" aria-label="Loading sign-in settings">
        <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-title text-foreground">Sign-in providers</h2>
        <p className="mt-1 max-w-2xl text-sm leading-6 text-body">
          Let people sign up and sign in with Google or Apple. A provider switches on as soon as its
          ID is saved, and clearing the ID switches it off. New accounts start as pending and go
          through onboarding, just like email sign-ups.
        </p>
      </div>

      <ProviderCard
        title="Google"
        status={!!field("google_client_id")?.is_set}
        docsHref="https://developers.google.com/identity/gsi/web/guides/get-google-api-clientid"
        steps={[
          "In Google Cloud Console, open APIs & Services → Credentials.",
          "Create an OAuth client ID of type “Web application”.",
          <>Under Authorised JavaScript origins, add the address below (and any other domain the site runs on).</>,
          "Copy the client ID and paste it here.",
        ]}
      >
        {origin && <CopyValue label="Authorised JavaScript origin" value={origin} />}
        {input(
          "google_client_id",
          "Google client ID",
          "1234567890-abc123.apps.googleusercontent.com",
          "Ends in .apps.googleusercontent.com. This ID is public; no secret is needed.",
        )}
      </ProviderCard>

      <ProviderCard
        title="Apple"
        status={!!field("apple_client_id")?.is_set}
        docsHref="https://developer.apple.com/help/account/configure-app-capabilities/configure-sign-in-with-apple-for-the-web"
        steps={[
          "In your Apple Developer account, create a Services ID under Certificates, Identifiers & Profiles.",
          "Turn on Sign in with Apple for it and click Configure.",
          <>Add your domain and the return URL below to the Services ID.</>,
          "Paste the Services ID (it looks like com.yourcompany.web) here.",
        ]}
      >
        {appleReturn && <CopyValue label="Return URL to register with Apple" value={appleReturn} />}
        {input(
          "apple_client_id",
          "Apple Services ID",
          "com.taskpart.web",
          "The identifier of the Services ID, not your team or key ID.",
        )}
        {input(
          "apple_redirect_uri",
          "Return URL (optional)",
          appleReturn || "https://taskpart.org/auth/login",
          "Leave blank to use the address above. Must match a return URL registered with Apple.",
        )}
      </ProviderCard>

      <Button disabled={!isDirty || saving} loading={saving} onClick={handleSave}>
        {!saving && <Save />}
        Save sign-in settings
      </Button>
    </div>
  );
}
